<template>
    <form @submit.prevent="submitForm">
        <div v-if="error" class="error-message-gcn">{{ error }}</div>
        <div v-if="loadingTickerData" class="loading-indicator-gcn small">Cargando datos del ticker...</div>

        <!-- Ticker Autocomplete -->
        <div class="form-group-gcn">
            <label for="op-import-ticker">Ticker <span class="required">*</span></label>
            <input
                type="text"
                id="op-import-ticker"
                class="form-control-gcn"
                :class="{ 'error': shouldShowError('ticker') }"
                v-model.trim="tickerSearch"
                @input="debounceFetchAutocomplete"
                @blur="markFieldTouched('ticker'); validateTicker()"
                @focus="markFieldTouched('ticker')"
                placeholder="Buscar ticker (e.g., AAPL)"
                required
                :disabled="isSubmitting"
                autocomplete="off"
            />
            <ul v-if="showSuggestions && tickerSuggestions.length > 0" class="autocomplete-suggestions">
                <li v-for="suggestion in tickerSuggestions" :key="suggestion.ticker" @mousedown.prevent="selectTicker(suggestion.ticker)">
                    {{ suggestion.ticker }} - {{ suggestion.name }}
                </li>
            </ul>
             <input type="hidden" v-model="formData.ticker" />
            <small v-if="shouldShowError('ticker')" class="error-message-gcn">{{ tickerError }}</small>
            <small v-else-if="formData.ticker" class="text-secondary">Ticker seleccionado: {{ formData.ticker }}</small>
        </div>

         <!-- Direction, Entry Type, Instrument Type -->
         <div class="form-group-gcn">
            <label>Dirección <span class="required">*</span></label>
             <div>
                <label class="radio-label text-secondary">
                    <input type="radio" v-model="formData.direction" value="1" :disabled="isSubmitting || loadingTickerData" @change="handleTickerInfoChange"> Alcista (Largo)
                </label>
                 <label class="radio-label text-secondary">
                    <input type="radio" v-model="formData.direction" value="0" :disabled="isSubmitting || loadingTickerData" @change="handleTickerInfoChange"> Bajista (Corto)
                </label>
            </div>
        </div>
        
         <div class="form-group-gcn">
            <label for="op-import-entry-type">Tipo de Entrada <span class="required">*</span></label>
            <select 
                id="op-import-entry-type" 
                class="form-control-gcn" 
                :class="{ 'error': shouldShowError('entryType') }"
                v-model="formData.entry_type" 
                @change="markFieldTouched('entryType'); validateEntryType()"
                @focus="markFieldTouched('entryType')"
                required 
                :disabled="!tickerInfo.entries || isSubmitting || loadingTickerData">
                <option v-if="!tickerInfo.entries" value="" disabled>Selecciona Ticker y Dirección</option>
                <option v-for="entry in availableEntryTypes" :key="entry.value" :value="entry.value">
                    {{ entry.name }}
                </option>
            </select>
            <small v-if="shouldShowError('entryType')" class="error-message-gcn">{{ entryTypeError }}</small>
        </div>
        
        <div class="form-group-gcn">
            <label for="op-import-instrument-type">Tipo de Instrumento <span class="required">*</span></label>
             <select id="op-import-instrument-type" class="form-control-gcn" v-model="formData.instrument_type" required :disabled="isSubmitting || loadingTickerData">
                 <option value="STOCK">Acción (Stock)</option>
                 <option value="CFD">CFD</option>
            </select>
        </div>

        <!-- Currency and Exchange Rate -->
         <div class="form-group-gcn">
             <label for="op-import-currency">Moneda Instrumento <span class="required">*</span></label>
             <input 
                 type="text" 
                 id="op-import-currency" 
                 class="form-control-gcn" 
                 :class="{ 'error': shouldShowError('currency') }"
                 v-model="formData.currency" 
                 @input="validateCurrency"
                 @blur="markFieldTouched('currency'); validateCurrency()"
                 @focus="markFieldTouched('currency')"
                 :readonly="!!tickerInfo.currency" 
                 required
                 maxlength="3"
                 placeholder="USD"
                 style="text-transform: uppercase;">
             <small v-if="!tickerInfo.currency" class="text-secondary">Introduce el código de 3 letras (e.g., USD)</small>
             <small v-if="shouldShowError('currency')" class="error-message-gcn">{{ currencyError }}</small>
         </div>
         
        <div class="form-group-gcn">
            <label for="op-import-in-currency">Tipo Cambio Entrada <span class="required">*</span></label>
             <input 
                 type="text" 
                 id="op-import-in-currency" 
                 class="form-control-gcn" 
                 :class="{ 'error': shouldShowError('inCurrency') }"
                 :value="formatDisplayValue(formData.in_currency)"
                 @input="event => handleNumericInput(event, 'in_currency')"
                 @blur="event => { markFieldTouched('inCurrency'); formatNumericOnBlur(event, 'in_currency'); }"
                 @focus="event => { markFieldTouched('inCurrency'); handleFocus(event, 'in_currency'); }"
                 required
                 placeholder="1.00">
             <small class="text-secondary">Tipo de cambio Instrumento/Cartera en la fecha de entrada.</small>
             <small v-if="shouldShowError('inCurrency')" class="error-message-gcn">{{ inCurrencyError }}</small>
         </div>
         
        <div class="form-group-gcn">
                            <label for="op-import-instrument-number">Número Acciones <span class="required">*</span></label>
             <input 
                 type="text" 
                 id="op-import-instrument-number" 
                 class="form-control-gcn" 
                 :class="{ 'error': shouldShowError('instrumentNumber') }"
                 :value="formatDisplayValue(formData.instrument_number, false)"
                 @input="event => handleNumericInput(event, 'instrument_number', false)"
                 @blur="event => { markFieldTouched('instrumentNumber'); formatNumericOnBlur(event, 'instrument_number'); }"
                 @focus="event => { markFieldTouched('instrumentNumber'); handleFocus(event, 'instrument_number'); }"
                 required
                 placeholder="100">
             <small class="text-secondary">Número de acciones o contratos.</small>
             <small v-if="shouldShowError('instrumentNumber')" class="error-message-gcn">{{ instrumentNumberError }}</small>
         </div>
         
        <div class="form-group-gcn">
            <label for="op-import-in-price">Precio Entrada <span class="required">*</span></label>
             <input 
                 type="text" 
                 id="op-import-in-price" 
                 class="form-control-gcn" 
                 :class="{ 'error': shouldShowError('inPrice') }"
                 :value="formatDisplayValue(formData.in_price)"
                 @input="event => handleNumericInput(event, 'in_price')"
                 @blur="event => { markFieldTouched('inPrice'); formatNumericOnBlur(event, 'in_price'); }"
                 @focus="event => { markFieldTouched('inPrice'); handleFocus(event, 'in_price'); }"
                 required
                 placeholder="0.00">
             <small v-if="shouldShowError('inPrice')" class="error-message-gcn">{{ inPriceError }}</small>
         </div>
         
        <div class="form-group-gcn">
             <label for="op-import-in-date">Fecha Entrada <span class="required">*</span></label>
             <input 
                 type="date" 
                 id="op-import-in-date" 
                 class="form-control-gcn" 
                 :class="{ 'error': shouldShowError('inDate') }"
                 :value="formattedInDateForInput" 
                 @input="updateInDate" 
                 @blur="markFieldTouched('inDate'); validateInDate()"
                 @focus="markFieldTouched('inDate')"
                 required>
             <small class="text-secondary">Formato API: d-m-Y ({{ formData.in_date }})</small>
             <small v-if="shouldShowError('inDate')" class="error-message-gcn">{{ inDateError }}</small>
         </div>
         
         <div class="form-group-gcn">
            <label for="op-import-support-price">Precio Soporte <span class="required">*</span></label>
             <input 
                 type="text" 
                 id="op-import-support-price" 
                 class="form-control-gcn" 
                 :class="{ 'error': shouldShowError('supportPrice') }"
                 :value="formatDisplayValue(formData.support_price)"
                 @input="event => handleNumericInput(event, 'support_price')"
                 @blur="event => { markFieldTouched('supportPrice'); formatNumericOnBlur(event, 'support_price'); }"
                 @focus="event => { markFieldTouched('supportPrice'); handleFocus(event, 'support_price'); }"
                 required
                 placeholder="0.00">
             <small v-if="shouldShowError('supportPrice')" class="error-message-gcn">{{ supportPriceError }}</small>
         </div>
         
         <div class="form-group-gcn">
             <label for="op-import-support-date">Fecha Soporte <span class="required">*</span></label>
             <input 
                 type="date" 
                 id="op-import-support-date" 
                 class="form-control-gcn" 
                 :class="{ 'error': shouldShowError('supportDate') }"
                 :value="formattedSupportDateForInput" 
                 @input="updateSupportDate" 
                 @blur="markFieldTouched('supportDate'); validateSupportDate()"
                 @focus="markFieldTouched('supportDate')"
                 required>
             <small class="text-secondary">Formato API: d-m-Y ({{ formData.support_date }})</small>
             <small v-if="shouldShowError('supportDate')" class="error-message-gcn">{{ supportDateError }}</small>
         </div>
         
          <div class="form-group-gcn">
             <label for="op-import-is-ipo">¿Es IPO? <span class="required">*</span></label>
              <select id="op-import-is-ipo" class="form-control-gcn" v-model="formData.is_ipo" required :disabled="isSubmitting">
                 <option value="0">No</option>
                 <option value="1">Sí</option>
             </select>
         </div>
         
          <div class="form-group-gcn">
            <label for="op-import-cfd-margin">Margen CFD <span class="required">*</span></label>
             <input 
                 type="text" 
                 id="op-import-cfd-margin" 
                 class="form-control-gcn" 
                 :class="{ 'error': shouldShowError('cfdMargin') }"
                 :value="formatDisplayValue(formData.cfd_margin)"
                 @input="event => handleNumericInput(event, 'cfd_margin')"
                 @blur="event => { markFieldTouched('cfdMargin'); formatNumericOnBlur(event, 'cfd_margin'); }"
                 @focus="event => { markFieldTouched('cfdMargin'); handleFocus(event, 'cfd_margin'); }"
                 required 
                 :disabled="isSubmitting"
                 placeholder="0.00">
              <small class="text-secondary">Introduce 0 si es una Acción.</small>
              <small v-if="shouldShowError('cfdMargin')" class="error-message-gcn">{{ cfdMarginError }}</small>
         </div>
         
         <div class="form-group-gcn">
            <label for="op-import-open-fee">Comisión Apertura <span class="required">*</span></label>
             <input 
                 type="text" 
                 id="op-import-open-fee" 
                 class="form-control-gcn" 
                 :class="{ 'error': shouldShowError('openFee') }"
                 :value="formatDisplayValue(formData.open_fee)"
                 @input="event => handleNumericInput(event, 'open_fee')"
                 @blur="event => { markFieldTouched('openFee'); formatNumericOnBlur(event, 'open_fee'); }"
                 @focus="event => { markFieldTouched('openFee'); handleFocus(event, 'open_fee'); }"
                 required
                 placeholder="0.00">
             <small class="text-secondary">Comisión pagada al abrir (en moneda de la cartera).</small>
             <small v-if="shouldShowError('openFee')" class="error-message-gcn">{{ openFeeError }}</small>
         </div>

        <!-- Form Status Indicator -->
        <div v-if="isFormValid && submitAttempted" class="form-status success">
            <span class="status-icon">✓</span> Formulario completado correctamente
        </div>
        <div v-else-if="hasErrors && submitAttempted" class="form-status error">
            <span class="status-icon">⚠</span> Por favor, corrija los errores antes de continuar
         </div>

        <!-- Hidden submit button -->
        <button type="submit" style="display: none;"></button>
    </form>
</template>

<script>
import apiService, { formatDateDMY, parseDate } from './apiService';
import _ from 'lodash';

const ENTRY_TYPES = { 0: 'RR', 1: 'RRA', 2: 'LT', 3: 'LTA', 6: 'Tipo 6?', 7: 'Tipo 7?' };

export default {
    name: 'OpenPositionImportForm',
    props: {
        portfolioId: { type: String, required: true },
        error: { type: String, default: null },
        isSubmitting: { type: Boolean, default: false }
    },
    data() {
        return {
            loadingTickerData: false,
            tickerSearch: '',
            formData: {
                ticker: '',
                entry_type: '',
                direction: '1',
                instrument_type: 'STOCK',
                currency: '',
                in_currency: 1,
                instrument_number: null,
                in_price: null,
                in_date: formatDateDMY(new Date()),
                support_price: null,
                support_date: formatDateDMY(new Date()),
                is_ipo: '0',
                cfd_margin: 0,
                open_fee: 0,
            },
            tickerSuggestions: [],
            showSuggestions: false,
            tickerInfo: { entries: null, currency: null },
            debounceFetchAutocomplete: null,
            // Validation errors
            tickerError: null,
            entryTypeError: null,
            currencyError: null,
            inCurrencyError: null,
            instrumentNumberError: null,
            inPriceError: null,
            inDateError: null,
            supportPriceError: null,
            supportDateError: null,
            cfdMarginError: null,
            openFeeError: null,
            // Validation states
            tickerValid: false,
            entryTypeValid: false,
            currencyValid: false,
            inCurrencyValid: false,
            instrumentNumberValid: false,
            inPriceValid: false,
            inDateValid: false,
            supportPriceValid: false,
            supportDateValid: false,
            cfdMarginValid: false,
            openFeeValid: false,
            // Touch states
            touchedFields: {
                ticker: false,
                entryType: false,
                currency: false,
                inCurrency: false,
                instrumentNumber: false,
                inPrice: false,
                inDate: false,
                supportPrice: false,
                supportDate: false,
                cfdMargin: false,
                openFee: false
            },
            submitAttempted: false
        };
    },
     computed: {
         availableEntryTypes() {
            if (!this.tickerInfo.entries) return [];
            return this.tickerInfo.entries.map(entry => ({
                value: entry.tipus_entrada,
                name: ENTRY_TYPES[entry.tipus_entrada] || `Tipo ${entry.tipus_entrada}`
            }));
        },
        formattedInDateForInput() { return this.formatDateForInput(this.formData.in_date); },
        formattedSupportDateForInput() { return this.formatDateForInput(this.formData.support_date); },
        isFormValid() {
            return this.tickerValid && this.entryTypeValid && this.currencyValid && 
                   this.inCurrencyValid && this.instrumentNumberValid && this.inPriceValid && 
                   this.inDateValid && this.supportPriceValid && this.supportDateValid && 
                   this.cfdMarginValid && this.openFeeValid && !this.hasErrors;
        },
        hasErrors() {
            return !!(this.tickerError || this.entryTypeError || this.currencyError || 
                     this.inCurrencyError || this.instrumentNumberError || this.inPriceError || 
                     this.inDateError || this.supportPriceError || this.supportDateError || 
                     this.cfdMarginError || this.openFeeError);
        }
    },
    created() {
        this.debounceFetchAutocomplete = _.debounce(this.fetchAutocomplete, 300);
        this.validateInitialValues();
    },
     methods: {
        markFieldTouched(fieldName) {
            this.touchedFields[fieldName] = true;
        },

        shouldShowError(fieldName) {
            return (this.touchedFields[fieldName] || this.submitAttempted) && this[fieldName + 'Error'];
        },

        validateInitialValues() {
            this.validateCurrency();
            this.validateInCurrency();
            this.validateCfdMargin();
            this.validateOpenFee();
            this.validateInDate();
            this.validateSupportDate();
        },

        formatDateForInput(dmyDate) {
            if (!dmyDate) return '';
            const date = parseDate(dmyDate);
            if (!date) return '';
            return date.toISOString().split('T')[0];
        },
        
        updateDate(field, event) {
            const dateValue = event.target.value;
             this.formData[field] = dateValue ? formatDateDMY(new Date(dateValue + 'T00:00:00')) : '';
        },
        updateInDate(event) { 
            this.updateDate('in_date', event); 
            this.validateInDate();
        },
        updateSupportDate(event) { 
            this.updateDate('support_date', event); 
            this.validateSupportDate();
        },

        validateTicker() {
            if (!this.formData.ticker || !this.tickerSearch) {
                this.tickerError = 'Debe seleccionar un ticker válido.';
                this.tickerValid = false;
            } else {
                this.tickerError = null;
                this.tickerValid = true;
            }
        },

        validateEntryType() {
            if (!this.formData.entry_type) {
                this.entryTypeError = 'Debe seleccionar un tipo de entrada.';
                this.entryTypeValid = false;
            } else {
                this.entryTypeError = null;
                this.entryTypeValid = true;
            }
        },

        validateCurrency() {
            if (!this.formData.currency) {
                this.currencyError = 'La moneda es requerida.';
                this.currencyValid = false;
            } else if (this.formData.currency.length !== 3) {
                this.currencyError = 'La moneda debe tener exactamente 3 caracteres.';
                this.currencyValid = false;
            } else if (!/^[A-Z]{3}$/.test(this.formData.currency)) {
                this.currencyError = 'La moneda debe contener solo letras mayúsculas.';
                this.currencyValid = false;
            } else {
                this.currencyError = null;
                this.currencyValid = true;
            }
        },

        validateNumericField(value, fieldName, canBeZero = true, isInteger = false) {
            const numericValue = parseFloat(value);
            
            if (value === null || value === undefined || value === '' || isNaN(numericValue)) {
                return `${fieldName} es requerido y debe ser un número válido.`;
            }
            
            if (numericValue < 0) {
                return `${fieldName} no puede ser negativo.`;
            }
            
            if (!canBeZero && numericValue === 0) {
                return `${fieldName} debe ser mayor que 0.`;
            }
            
            if (isInteger && !Number.isInteger(numericValue)) {
                return `${fieldName} debe ser un número entero.`;
            }
            
            return null;
        },

        validateInCurrency() {
            const error = this.validateNumericField(this.formData.in_currency, 'Tipo de cambio', false);
            this.inCurrencyError = error;
            this.inCurrencyValid = !error;
        },

        validateInstrumentNumber() {
            const error = this.validateNumericField(this.formData.instrument_number, 'Número de acciones', false, true);
            this.instrumentNumberError = error;
            this.instrumentNumberValid = !error;
        },

        validateInPrice() {
            const error = this.validateNumericField(this.formData.in_price, 'Precio de entrada', false);
            this.inPriceError = error;
            this.inPriceValid = !error;
        },

        validateSupportPrice() {
            const error = this.validateNumericField(this.formData.support_price, 'Precio de soporte', false);
            this.supportPriceError = error;
            this.supportPriceValid = !error;
        },

        validateCfdMargin() {
            const error = this.validateNumericField(this.formData.cfd_margin, 'Margen CFD', true);
            this.cfdMarginError = error;
            this.cfdMarginValid = !error;
        },

        validateOpenFee() {
            const error = this.validateNumericField(this.formData.open_fee, 'Comisión de apertura', true);
            this.openFeeError = error;
            this.openFeeValid = !error;
        },

        validateDate(dateValue, fieldName) {
            if (!dateValue) {
                return `${fieldName} es requerida.`;
            }
            
            const parsedDate = parseDate(dateValue);
            if (!parsedDate) {
                return `${fieldName} no es una fecha válida.`;
            }
            
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            if (parsedDate > today) {
                return `${fieldName} no puede ser futura.`;
            }
            
            return null;
        },

        validateInDate() {
            const error = this.validateDate(this.formData.in_date, 'Fecha de entrada');
            this.inDateError = error;
            this.inDateValid = !error;
        },

        validateSupportDate() {
            const error = this.validateDate(this.formData.support_date, 'Fecha de soporte');
            this.supportDateError = error;
            this.supportDateValid = !error;
            
            // Additional validation: support date should not be before entry date
            if (!error && this.inDateValid) {
                const entryDate = parseDate(this.formData.in_date);
                const supportDate = parseDate(this.formData.support_date);
                if (supportDate < entryDate) {
                    this.supportDateError = 'La fecha de soporte no puede ser anterior a la fecha de entrada.';
                    this.supportDateValid = false;
                }
            }
        },

        formatDisplayValue(value, includeDecimals = true) {
            if (value === null || value === undefined) return '';
            if (typeof value === 'number') {
                if (includeDecimals) {
                    return value.toFixed(2);
                } else {
                    return value.toString();
                }
            }
            return value;
        },

        handleFocus(event, modelKey) {
            if (this.formData[modelKey] !== null && this.formData[modelKey] !== undefined) {
                event.target.value = this.formData[modelKey].toString();
            }
        },

        handleNumericInput(event, modelKey, allowDecimals = true) {
            let value = event.target.value;
            let sanitizedValue;
            
            if (allowDecimals) {
                // Only allow digits, dots, and commas (no dashes/minus signs)
                sanitizedValue = value.replace(/[^\d.,]/g, '');
                
                // Handle decimal separators: find the last comma or dot as decimal separator
                const lastCommaIndex = sanitizedValue.lastIndexOf(',');
                const lastDotIndex = sanitizedValue.lastIndexOf('.');
                const lastDecimalIndex = Math.max(lastCommaIndex, lastDotIndex);
                
                if (lastDecimalIndex !== -1) {
                    // Split into integer and decimal parts
                    const integerPart = sanitizedValue.substring(0, lastDecimalIndex).replace(/[.,]/g, ''); // Remove all dots/commas from integer part
                    const decimalPart = sanitizedValue.substring(lastDecimalIndex + 1);
                    
                    // Limit decimal part to 2 digits
                    const limitedDecimalPart = decimalPart.substring(0, 2);
                    
                    // Reconstruct with dot as decimal separator
                    // Always include the dot if there was a decimal separator, even if decimal part is empty
                    sanitizedValue = integerPart + '.' + limitedDecimalPart;
                }
                
                // Prevent starting with decimal point - require leading zero
                if (sanitizedValue.startsWith('.')) {
                    sanitizedValue = '0' + sanitizedValue;
                }
            } else {
                // Only allow digits (no dashes/minus signs)
                sanitizedValue = value.replace(/[^\d]/g, '');
            }

            event.target.value = sanitizedValue;

            if (sanitizedValue === '') {
                this.formData[modelKey] = null;
            } else if (!isNaN(parseFloat(sanitizedValue)) && isFinite(sanitizedValue)) {
                this.formData[modelKey] = sanitizedValue;
            } else if (allowDecimals && sanitizedValue.endsWith('.')) {
                this.formData[modelKey] = sanitizedValue;
            } else {
                this.formData[modelKey] = null;
            }

            // Real-time validation
            switch(modelKey) {
                case 'in_currency':
                    this.validateInCurrency();
                    break;
                case 'instrument_number':
                    this.validateInstrumentNumber();
                    break;
                case 'in_price':
                    this.validateInPrice();
                    break;
                case 'support_price':
                    this.validateSupportPrice();
                    break;
                case 'cfd_margin':
                    this.validateCfdMargin();
                    break;
                case 'open_fee':
                    this.validateOpenFee();
                    break;
            }
        },

        formatNumericOnBlur(event, modelKey) {
            let value = this.formData[modelKey];
            
            if (value === null || String(value).trim() === '') {
                this.formData[modelKey] = null;
                return;
            }
            
            if(typeof value === 'string' && value.endsWith('.')) {
                value = value.slice(0, -1);
            }

            const numericValue = parseFloat(value);
            if (!isNaN(numericValue) && isFinite(numericValue)) {
                // Round to 2 decimal places for decimal fields
                const roundedValue = modelKey === 'instrument_number' ? 
                    Math.round(numericValue) : 
                    Math.round(numericValue * 100) / 100;
                this.formData[modelKey] = roundedValue;
            } else {
                this.formData[modelKey] = null;
            }

            // Validate after formatting
            switch(modelKey) {
                case 'in_currency':
                    this.validateInCurrency();
                    break;
                case 'instrument_number':
                    this.validateInstrumentNumber();
                    break;
                case 'in_price':
                    this.validateInPrice();
                    break;
                case 'support_price':
                    this.validateSupportPrice();
                    break;
                case 'cfd_margin':
                    this.validateCfdMargin();
                    break;
                case 'open_fee':
                    this.validateOpenFee();
                    break;
            }
        },

        async fetchAutocomplete() {
             if (this.tickerSearch.length < 2) { 
                 this.tickerSuggestions = []; 
                 this.showSuggestions = false; 
                 return; 
             }
            try {
                const results = await apiService.getTickerAutocomplete(this.tickerSearch);
                this.tickerSuggestions = Object.entries(results).map(([ticker, valueString]) => {
                    const parts = valueString.split(',');
                    return { ticker: ticker, name: parts[1] || '' };
                });
                this.showSuggestions = true;
            } catch (err) { 
                console.error("Autocomplete error:", err); 
                this.tickerSuggestions = []; 
                this.showSuggestions = false; 
            }
        },
        
        selectTicker(ticker) {
            this.tickerSearch = ticker;
            this.formData.ticker = ticker;
            this.showSuggestions = false;
            this.tickerSuggestions = [];
            this.validateTicker();
            this.fetchTickerInfo();
        },
        
         clearSuggestions() { 
             setTimeout(() => { 
                 this.showSuggestions = false; 
             }, 150); 
         },
         
        async fetchTickerInfo() {
             if (!this.formData.ticker || !this.formData.direction) return;
            this.loadingTickerData = true;
            this.tickerInfo = { entries: null, currency: null };
            this.formData.entry_type = '';
             this.formData.currency = '';
            try {
                const data = await apiService.getTickerData(this.formData.ticker, this.formData.direction);
                 if (data && data.currency !== false) {
                     this.tickerInfo = data;
                     this.formData.currency = data.currency;
                     this.validateCurrency();
                     this.validateEntryType();
                 } else {
                     this.$emit('error', `No se encontró info (moneda/entradas) para ${this.formData.ticker}. Introduce la moneda manualmente.`);
                 }
            } catch (err) { 
                this.$emit('error', `Error buscando datos para ${this.formData.ticker}: ${err.message}`); 
            }
             finally { 
                 this.loadingTickerData = false; 
             }
        },
        
         handleTickerInfoChange() { 
             if (this.formData.ticker) { 
                 this.fetchTickerInfo(); 
             } 
         },

        submitForm() {
            this.submitAttempted = true;
            
            // Mark all fields as touched
            Object.keys(this.touchedFields).forEach(field => {
                this.markFieldTouched(field);
            });

            // Validate all fields before submission
            this.validateTicker();
            this.validateEntryType();
            this.validateCurrency();
            this.validateInCurrency();
            this.validateInstrumentNumber();
            this.validateInPrice();
            this.validateInDate();
            this.validateSupportPrice();
            this.validateSupportDate();
            this.validateCfdMargin();
            this.validateOpenFee();

            if (!this.isFormValid || this.hasErrors) {
                this.$emit('error', 'Por favor, corrija los errores en el formulario antes de continuar.');
                         return;
            }

            // Additional business rule validations
            if (this.formData.instrument_type === 'STOCK' && this.formData.cfd_margin > 0) {
                this.$emit('error', 'El margen CFD debe ser 0 para Acciones.');
                return;
            }

            this.$emit('submit', { ...this.formData });
        }
    },
     watch: {
        tickerSearch(newVal) {
            if (!newVal) {
                this.formData.ticker = '';
                this.tickerInfo = { entries: null, currency: null };
                this.formData.entry_type = '';
                this.formData.currency = '';
                this.tickerValid = false;
                this.entryTypeValid = false;
                this.currencyValid = false;
                this.touchedFields.ticker = false;
                this.touchedFields.entryType = false;
                this.touchedFields.currency = false;
                this.validateTicker();
                this.validateEntryType();
                this.validateCurrency();
            }
        },
        
        'formData.instrument_type'(newType) {
            if (newType === 'STOCK') {
                this.formData.cfd_margin = 0;
                this.validateCfdMargin();
            }
        },

        'formData.currency'(newVal) {
            if (newVal) {
                this.formData.currency = newVal.toUpperCase();
            }
        }
    }
}
</script>

<style scoped>
.autocomplete-suggestions {
    position: absolute;
    background-color: white;
    border: 1px solid #ccc;
    box-shadow: 0 2px 5px rgba(0,0,0,0.15);
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 150px;
    overflow-y: auto;
    z-index: 1051;
    width: calc(100% - 2px);
}
.autocomplete-suggestions li { 
    padding: 8px 12px; 
    cursor: pointer; 
}
.autocomplete-suggestions li:hover { 
    background-color: #f0f0f0; 
}
.loading-indicator-gcn.small { 
    font-size: 0.9em; 
    text-align: left; 
    padding: 5px 0; 
}

/* Enhanced validation styles */
.required {
    color: var(--color-error);
    font-weight: bold;
}

.error-message-gcn {
    color: var(--color-error);
    font-size: 0.8em;
    display: block;
    margin-top: 4px;
}

.text-secondary {
    color: var(--color-text-secondary);
}

.radio-label {
    margin-right: 15px; 
    font-weight: normal;
}

.form-control-gcn.error {
    border-color: var(--color-error);
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}

.form-status {
    padding: 10px;
    border-radius: 4px;
    margin-top: 15px;
    font-size: 0.9em;
    display: flex;
    align-items: center;
    gap: 8px;
}

.form-status.success {
    background-color: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
}

.form-status.error {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
}

.status-icon {
    font-weight: bold;
    font-size: 1.1em;
}
</style>